import { useState } from "react";
import { Section } from "../../../types";
import { sortPicturesByPosition, sortSectionsByPosition } from "./Dashboard";
import { getHighestPicturePosition, getHighestPosition } from "./util";
import DashboardImageSection from "./DashboardImageSection";
import DashboardTextSection from "./DashboardTextSection";
import { savePage } from "../../util/api";

const DashboardSections = ({
  data,
  textAmount,
  imageAmount,
  pageId,
  pageName,
}: {
  data: Section[];
  textAmount: number;
  imageAmount: number;
  pageId: number;
  pageName: string;
}) => {
  const [editableData, setEditableData] = useState<Section[]>(data);
  const [indexCounter, setIndexCounter] = useState(-1);
  const [password, setPassword] = useState("");

  const handleSave = () => {
    savePage({
      page: {
        title: pageName,
        id: pageId,
        sections: editableData,
      },
      password,
    })
      .then((statusText) => {
        if (statusText === 200) {
          window.location.reload();
        }
      })
      .catch((e) => console.error(e));
  };

  const handleMoveSectionUp = (sectionId: number) => {
    setEditableData((prev) => {
      const newSections = [...prev];
      const sectionIndex = newSections.findIndex(
        (section) => section.id === sectionId
      );

      if (sectionIndex > 0) {
        const section = newSections[sectionIndex];
        const aboveSection = newSections[sectionIndex - 1];

        // Swap the position properties of the two sections
        const tempPosition = section.position;
        section.position = aboveSection.position;
        aboveSection.position = tempPosition;
      }

      return sortSectionsByPosition(newSections);
    });
  };

  const handleMoveSectionDown = (sectionId: number) => {
    setEditableData((prev) => {
      const newSections = [...prev];
      const sectionIndex = newSections.findIndex(
        (section) => section.id === sectionId
      );

      if (sectionIndex < newSections.length - 1) {
        const section = newSections[sectionIndex];
        const belowSection = newSections[sectionIndex + 1];

        // Swap the position properties of the two sections
        const tempPosition = section.position;
        section.position = belowSection.position;
        belowSection.position = tempPosition;
      }

      return sortSectionsByPosition(newSections);
    });
  };

  const handleMovePictureUp = ({
    sectionId,
    pictureId,
  }: {
    sectionId: number;
    pictureId: number;
  }) => {
    setEditableData((prev) => {
      const newSections = [...prev];
      const sectionIndex = newSections.findIndex(
        (section) => section.id === sectionId
      );
      const section = { ...newSections[sectionIndex] }; // Create a new section object
      const pictureIndex = section.pictures.findIndex(
        (picture) => picture.id === pictureId
      );

      if (pictureIndex > 0) {
        const picture = { ...section.pictures[pictureIndex] }; // Create a new picture object
        const abovePicture = { ...section.pictures[pictureIndex - 1] }; // Create a new picture object

        // Swap the position properties of the two pictures
        const tempPosition = picture.position;
        picture.position = abovePicture.position;
        abovePicture.position = tempPosition;

        // Create a new pictures array with the updated pictures
        section.pictures = [
          ...section.pictures.slice(0, pictureIndex - 1),
          picture,
          abovePicture,
          ...section.pictures.slice(pictureIndex + 1),
        ];
      }

      return [
        ...newSections.slice(0, sectionIndex),
        {
          ...section,
          pictures: sortPicturesByPosition(section.pictures),
        },
        ...newSections.slice(sectionIndex + 1),
      ];
    });
  };

  const handleMovePictureDown = ({
    sectionId,
    pictureId,
  }: {
    sectionId: number;
    pictureId: number;
  }) => {
    setEditableData((prev) => {
      const newSections = [...prev];
      const sectionIndex = newSections.findIndex(
        (section) => section.id === sectionId
      );
      const section = { ...newSections[sectionIndex] }; // Create a new section object
      const pictureIndex = section.pictures.findIndex(
        (picture) => picture.id === pictureId
      );

      if (pictureIndex < section.pictures.length - 1) {
        const picture = { ...section.pictures[pictureIndex] }; // Create a new picture object
        const belowPicture = { ...section.pictures[pictureIndex + 1] }; // Create a new picture object

        // Swap the position properties of the two pictures
        const tempPosition = picture.position;
        picture.position = belowPicture.position;
        belowPicture.position = tempPosition;

        // Create a new pictures array with the updated pictures
        section.pictures = [
          ...section.pictures.slice(0, pictureIndex),
          belowPicture,
          picture,
          ...section.pictures.slice(pictureIndex + 2),
        ];
      }

      return [
        ...newSections.slice(0, sectionIndex),
        {
          ...section,
          pictures: sortPicturesByPosition(section.pictures),
        },
        ...newSections.slice(sectionIndex + 1),
      ];
    });
  };

  const handleAddSection = () => {
    setEditableData((prev) => {
      const newSections = [...prev];
      const currentHighestPosition = getHighestPosition(newSections);
      const newSection: Section = {
        id: indexCounter,
        position: currentHighestPosition + 1,
        texts: [],
        pictures: [],
      };

      const newTexts = [];
      for (let i = 0; i < textAmount; i++) {
        newTexts.push({
          id: indexCounter - i - 1,
          position: i,
          content: "",
        });
      }

      newSection.texts = newTexts;

      return sortSectionsByPosition([...newSections, newSection]);
    });
    setIndexCounter((prev) => prev - (textAmount + 1));
  };

  const handleAddPicture = (sectionId: number) => {
    setEditableData((prev) => {
      const newSections = [...prev];
      const sectionIndex = newSections.findIndex(
        (section) => section.id === sectionId
      );
      const section = newSections[sectionIndex];
      const currentHighestPosition = getHighestPicturePosition(
        newSections,
        sectionId
      );
      const newPicture = {
        id: indexCounter,
        position: currentHighestPosition + 1,
        description: "",
        dataUrl: "",
      };

      section.pictures.push(newPicture);
      newSections[sectionIndex] = section;
      return newSections;
    });
    setIndexCounter((prev) => prev - 1);
  };

  const handleRemoveSection = (sectionId: number) => {
    setEditableData((prev) => {
      return prev.filter((section) => section.id !== sectionId);
    });
  };

  const handleRemovePicture = ({
    sectionId,
    pictureId,
  }: {
    sectionId: number;
    pictureId: number;
  }) => {
    setEditableData((prev) => {
      const newSections = [...prev];
      const sectionIndex = newSections.findIndex(
        (section) => section.id === sectionId
      );
      const section = newSections[sectionIndex];

      return sortSectionsByPosition([
        ...newSections.slice(0, sectionIndex),
        {
          ...section,
          pictures: section.pictures.filter(
            (picture) => picture.id !== pictureId
          ),
        },
        ...newSections.slice(sectionIndex + 1),
      ]);
    });
  };

  const handleUpdateText = (
    textId: number,
    sectionId: number,
    content: string
  ) => {
    setEditableData((prev) => {
      const newSections = [...prev];
      const sectionIndex = newSections.findIndex(
        (section) => section.id === sectionId
      );
      const section = newSections[sectionIndex];
      const textIndex = section.texts.findIndex((text) => text.id === textId);
      const newTexts = [...section.texts];
      newTexts[textIndex].content = content;

      return sortSectionsByPosition([
        ...newSections.slice(0, sectionIndex),
        {
          ...section,
          texts: newTexts,
        },
        ...newSections.slice(sectionIndex + 1),
      ]);
    });
  };

  const handleUpdatePicture = (
    sectionId: number,
    pictureId: number,
    dataUrl: string
  ) => {
    setEditableData((prev) => {
      const newSections = [...prev];
      const sectionIndex = newSections.findIndex(
        (section) => section.id === sectionId
      );
      const section = newSections[sectionIndex];
      const pictureIndex = section.pictures.findIndex(
        (picture) => picture.id === pictureId
      );
      const newPictures = [...section.pictures];
      newPictures[pictureIndex].dataUrl = dataUrl;

      return sortSectionsByPosition([
        ...newSections.slice(0, sectionIndex),
        {
          ...section,
          pictures: newPictures,
        },
        ...newSections.slice(sectionIndex + 1),
      ]);
    });
  };

  return (
    <div>
      <h1>{pageName}</h1>
      <button onClick={handleAddSection}>Sektion hinzufügen</button>
      <button onClick={handleSave} disabled={password === ""}>
        Speichern
      </button>
      Passwort:
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      {editableData.map((section) => (
        <div
          key={section.id}
          style={{ display: "flex", border: "1px solid white" }}
        >
          <div
            style={{
              border: "1px solid white",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <h3>Sektion-Id {section.id}</h3>
            <button onClick={() => handleRemoveSection(section.id)}>
              Sektion entfernen
            </button>
            <button onClick={() => handleMoveSectionUp(section.id)}>
              Sektion nach oben
            </button>
            <button onClick={() => handleMoveSectionDown(section.id)}>
              Sektion nach unten
            </button>
          </div>
          <DashboardTextSection
            pageId={pageId}
            data={section.texts}
            onUpdateText={(textId, content) =>
              handleUpdateText(textId, section.id, content)
            }
          />
          <DashboardImageSection
            data={section.pictures}
            imageAmount={imageAmount}
            sectionId={section.id}
            onMovePictureUp={(sectionId, pictureId) =>
              handleMovePictureUp({ sectionId, pictureId })
            }
            onMovePictureDown={(sectionId, pictureId) =>
              handleMovePictureDown({ sectionId, pictureId })
            }
            onAddPicture={handleAddPicture}
            onRemoveImage={(sectionId, pictureId) =>
              handleRemovePicture({ sectionId, pictureId })
            }
            onUpdatePicture={(sectionId, pictureId, dataUrl) =>
              handleUpdatePicture(sectionId, pictureId, dataUrl)
            }
          />
        </div>
      ))}
    </div>
  );
};

export default DashboardSections;
